import { RefObject } from 'react';
import polylabel from 'polylabel';
import { ICoordinates } from 'react-give-me-map';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import { MapRef } from 'react-map-gl';

export const useCoordinatesActions = (mapRef: RefObject<MapRef>, coordinates: ICoordinates[]) => {
    const getForAdd = (xyCoordinate: IXY, index: number) => {
        const newCoordinates = [...coordinates];
        const latLng = mapRef?.current?.unproject([xyCoordinate.x, xyCoordinate.y]);
        newCoordinates.splice(index, 0, latLng);
        return newCoordinates;
    };

    const getForUpdate = (xyCoordinate: IXY, index: number) => {
        const newCoordinates = [...coordinates];
        const latLng = mapRef?.current?.unproject([xyCoordinate.x, xyCoordinate.y]);
        newCoordinates[index] = { lat: latLng.lat, lng: latLng.lng };
        return newCoordinates;
    };

    const getForRemove = (index: number) => {
        return coordinates.filter((_, i) => i !== index);
    };

    const getForResize = (
        xyCoordinates: IXY[],
        nonRotatedXyCoordinates: IXY[],
        index: number,
        xOffset: number,
        yOffset: number
    ) => {
        const [centerX, centerY] = polylabel([xyCoordinates.map(({ x, y }) => [x, y])]);
        const center = { x: centerX, y: centerY };

        const getDistance = (p1: IXY, p2: IXY) => {
            const a = p1.x - p2.x;
            const b = p1.y - p2.y;
            return Math.sqrt(a * a + b * b);
        };

        const distanceFromCenter = getDistance(xyCoordinates[index], center);
        const newDistanceFromCenter = getDistance(
            {
                x: xyCoordinates[index].x + xOffset,
                y: xyCoordinates[index].y + yOffset
            },
            center
        );

        const scaleFactor = newDistanceFromCenter / distanceFromCenter;

        const newCoordinates = xyCoordinates.map((xyCoordinate) => ({
            x: centerX + scaleFactor * (xyCoordinate.x - centerX),
            y: centerY + scaleFactor * (xyCoordinate.y - centerY)
        }));

        const newNonRotatedCoordinates = nonRotatedXyCoordinates.map((xyCoordinate) => ({
            x: centerX + scaleFactor * (xyCoordinate.x - centerX),
            y: centerY + scaleFactor * (xyCoordinate.y - centerY)
        }));

        const [topLeft, topRight, bottomRight, bottomLeft] = newCoordinates;

        const cTopLeft = mapRef?.current?.unproject([topLeft.x, topLeft.y]);
        const cTopRight = mapRef?.current?.unproject([topRight.x, topRight.y]);
        const cBottomRight = mapRef?.current?.unproject([bottomRight.x, bottomRight.y]);
        const cBottomLeft = mapRef?.current?.unproject([bottomLeft.x, bottomLeft.y]);

        return {
            coordinates: [cTopLeft, cTopRight, cBottomRight, cBottomLeft],
            nonRotatedXyCoordinates: newNonRotatedCoordinates
        };
    };

    return {
        getForAdd,
        getForUpdate,
        getForRemove,
        getForResize
    };
};
